import moment from "moment";
import { comment, arrayOfComments } from "~/schemas/schema.comments.jsx";
import { taskList } from "~/schemas/schema.task_lists.jsx";
import * as schemaTasks from "~/schemas/schema.tasks.jsx";
import * as actionTypes from "~/store/action_types";
import { getCommentById, makeParentHasNewCommentsSelector } from "~/store/selectors/selector.comments.jsx";
import { getCommentListTopInViewportByParent } from "~/store/selectors/selector.lastVisit.jsx";
import { getLoggedUserId } from "~/store/selectors/selector.users.jsx";
import Angie from "~/utils/angie.jsx";
import { fetch } from "~/utils/fetch.jsx";
import { getValFromTagAttribute } from "~/utils/utils.jsx";
import { clearObjectHasNewComments } from "./actions.additionalObjectData.jsx";
import { fetchLastVisitTimestamp } from "./actions.lastVisit.jsx";
import * as trashActions from "./actions.trash.jsx";

export const addComment = (
  parentType,
  parentId,
  attributes,
  commentId = Angie.functions.random_hash(),
  createdOn = moment.utc().format(`X`)
) => {
  const slug = Angie.functions.slug(parentType);
  let mentionedIds = [];
  const values = getValFromTagAttribute(attributes.body, "data-user-id", 300);
  for (const value of values) {
    const ids = value.split(",").map((id) => parseInt(id));
    mentionedIds.push(...ids);
  }

  return {
    types: [actionTypes.ADD_COMMENT_LOADING, actionTypes.ADD_COMMENT_SUCCESS, actionTypes.ADD_COMMENT_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/comments/${slug}/${parentId}`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      commentId,
      parentType,
      parentId,
      mentionedIds,
      attributes: {
        ...attributes,
        created_on: parseInt(createdOn),
      },
      schema: {
        single: comment,
      },
    },
  };
};

export const editComment = (commentId, attributes) => {
  return {
    types: [actionTypes.EDIT_COMMENT_LOADING, actionTypes.EDIT_COMMENT_SUCCESS, actionTypes.EDIT_COMMENT_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/comments/${commentId}`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      commentId,
      attributes,
      schema: {
        single: comment,
      },
    },
  };
};

// @todo: testirati load comments
export const fetchComments = (parentId, parentType, page = 0) => {
  const slug = Angie.functions.slug(parentType);
  return {
    types: [actionTypes.FETCH_COMMENTS_LOADING, actionTypes.FETCH_COMMENTS_SUCCESS, actionTypes.FETCH_COMMENTS_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/comments/${slug}/${parentId}?page=${page}`,
      }),
    payload: {
      parentId,
      parentType,
      page,
      schema: arrayOfComments,
    },
  };
};

export const moveToTrash = (id, parentType, parentId) => {
  const objectType = `Comment`;
  return {
    ...trashActions.moveToTrash(objectType, id),
    types: [
      actionTypes.MOVE_COMMENT_TO_TRASH_LOADING,
      actionTypes.MOVE_COMMENT_TO_TRASH_SUCCESS,
      actionTypes.MOVE_COMMENT_TO_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      parentType,
      parentId,
      schema: {
        single: comment,
      },
    },
  };
};

export const retrySubmitComment = (id) => (dispatch, getState) => {
  const comment = getCommentById(getState(), id);
  const { body, attach_uploaded_files, drop_attached_files } = comment.toJS();

  const attributes = {
    body,
    attach_uploaded_files,
    drop_attached_files,
  };

  let action = Number.isInteger(id)
    ? editComment(id, attributes)
    : addComment(comment.parent_type, comment.parent_id, attributes, id);

  action.types[0] = actionTypes.RETRY_SUBMIT_COMMENT_LOADING;

  return dispatch({ ...action });
};

export const addCommentFromEventStream = (data) => (dispatch, getState) => {
  const loggedUserId = getLoggedUserId(getState());
  const prevent = getCommentListTopInViewportByParent(getState(), data.parent_type, data.parent_id);
  const getParentHasNewComments = makeParentHasNewCommentsSelector();
  const parentHasNewComments = getParentHasNewComments(getState(), data.parent_type, data.parent_id);

  return dispatch({
    type: actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_SUCCESS,
    response: data,
    schema: comment,
    id: data.id,
    loggedUserId,
    prevent,
    parentHasNewComments,
    parentType: data.parent_type,
    parentId: data.parent_id,
  });
};

export const updateCommentFromEventStream = (data) => {
  return {
    type: actionTypes.UPDATE_COMMENT_FROM_EVENT_STREAM_SUCCESS,
    response: data,
    schema: comment,
    id: data.id,
    parentType: data.parent_type,
    parentId: data.parent_id,
  };
};

export const addBigCommentFromEventStream = (data) => {
  return {
    types: [
      actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_LOADING,
      actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_SUCCESS,
      actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/${data.url_path}`,
      }),
    payload: {
      schema: {
        single: comment,
      },
      id: data.id,
    },
  };
};

export const updateCommentParentLastVisitTimestamp = (id, timestamp) => (dispatch, getState) => {
  const comment = getCommentById(getState(), id);

  if (comment) {
    dispatch(fetchLastVisitTimestamp(comment.get(`parent_id`), comment.get(`parent_type`), timestamp));
  }
};

export const clearCommentParentHasNewComments = (id) => (dispatch, getState) => {
  const comment = getCommentById(getState(), id);

  if (comment) {
    dispatch(clearObjectHasNewComments(comment.get(`parent_type`), comment.get(`parent_id`)));
  }
};

export const appendToParent = (id, payload) => {
  return {
    types: [
      actionTypes.APPEND_TO_PARENT_LOADING,
      actionTypes.APPEND_TO_PARENT_SUCCESS,
      actionTypes.APPEND_TO_PARENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        method: "POST",
        url: `${Angie.api_url}/comments/${id}/append-to-parent`,
        body: payload,
      }),
    payload,
  };
};

export const createTaskFromComment = (id, attributes) => {
  return {
    types: [
      actionTypes.CREATE_TASK_FROM_COMMENT_LOADING,
      actionTypes.CREATE_TASK_FROM_COMMENT_SUCCESS,
      actionTypes.CREATE_TASK_FROM_COMMENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        method: "POST",
        url: `${Angie.api_url}/comments/${id}/create-task`,
        body: attributes,
      }),
    payload: {
      schema: {
        single: schemaTasks.task,
        task_list: taskList,
      },
      id,
    },
  };
};
